import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { getSupabase } from "@dwelldrop/supabase";
import { useVenueAuth } from "./VenueAuthContext";
import { withRetry, type RedemptionSchedule, type VenueClosedDate } from "@dwelldrop/shared";

/** Venue statuses that allow normal operation */
const USABLE_STATUSES = ["active", "pending"];

interface Venue {
  id: string;
  name: string;
  category: string;
  address: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  description: string | null;
  logo_url: string | null;
  latitude: number | null;
  longitude: number | null;
  status: string;
  dwell_platform_fee: number | null;
  min_dwell_platform_fee: number | null;
  min_dwell_platform_fee_percentage: number | null;
  referring_venue_incentive: number | null;
  created_at: string;
  points_redemption_schedule?: RedemptionSchedule;
  closed_date?: VenueClosedDate;
  /** Only loaded for venue owners */
  agreement_url?: string | null;
}

interface VenueContextValue {
  /** All venues the current user can access */
  venues: Venue[];
  /** Currently selected venue */
  currentVenue: Venue | null;
  /** Switch to a different venue */
  switchVenue: (venueId: string) => void;
  /** Update a field on the current venue locally */
  patchCurrentVenue: (patch: Partial<Venue>) => void;
  /** Loading state */
  loading: boolean;
}

const VenueContext = createContext<VenueContextValue | null>(null);

export function useVenue(): VenueContextValue {
  const ctx = useContext(VenueContext);
  if (!ctx) throw new Error("useVenue must be used within VenueProvider");
  return ctx;
}

const LEGACY_SELECTED_VENUE_KEY = "dwelldrop_selected_venue";
const selectedVenueKey = (userId: string): string =>
  `dwelldrop_selected_venue:${userId}`;

export function VenueProvider({ children }: { children: ReactNode }) {
  const { user, profile, signOut } = useVenueAuth();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [currentVenue, setCurrentVenue] = useState<Venue | null>(null);
  const [loading, setLoading] = useState(true);

  const loadVenues = useCallback(async () => {
    if (!user || !profile) return;
    setLoading(true);
    const supabase = getSupabase();

    let venueList: Venue[] = [];

    if (profile?.role === "venue_owner") {
      // Venue owners: fetch ALL venues first to distinguish "no venues" from "all suspended"
      const data = await withRetry(async () => {
        const { data, error } = await supabase
          .from("venues")
          .select("id, name, category, address, address_line_1, address_line_2, description, logo_url, latitude, longitude, status, dwell_platform_fee, min_dwell_platform_fee, min_dwell_platform_fee_percentage, referring_venue_incentive, created_at, agreement_url, points_redemption_schedule, closed_date")
          .eq("owner_id", user!.id)
          .order("name");
        if (error) throw error;
        return data;
      });

      const allVenues = data ?? [];
      const usable = allVenues.filter((v) => USABLE_STATUSES.includes(v.status));

      if (allVenues.length > 0 && usable.length === 0) {
        // Owner has venues but all are suspended/closed
        console.warn("[VenueContext] All owner venues suspended — signing out");
        await signOut();
        window.location.replace("/login?suspended=true");
        return;
      }

      venueList = usable;
    } else if (profile?.role === "venue_staff") {
      // Staff see only assigned venues
      const data = await withRetry(async () => {
        const { data, error } = await supabase
          .from("venue_staff")
          .select(`
            venue:venues(id, name, category, address, address_line_1, address_line_2, description, logo_url, latitude, longitude, status, dwell_platform_fee, min_dwell_platform_fee, min_dwell_platform_fee_percentage, referring_venue_incentive, created_at, points_redemption_schedule, closed_date)
          `)
          .eq("user_id", user!.id)
          .eq("status", "active");
        if (error) throw error;
        return data;
      });

      venueList = (data ?? [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((d: any) => d.venue)
        .filter((v: Venue | null) => v && USABLE_STATUSES.includes(v.status)) as Venue[];
    } else if (profile?.role === "property_operator") {
      // Property operators: fetch venues across their properties
      console.log("[VenueContext] Fetching venues for property_operator:", user!.id);
      const data = await withRetry(async () => {
        const { data, error } = await supabase
          .from("property_operators")
          .select(`
            properties:property_id (
              venues (id, name, category, address, address_line_1, address_line_2, description, logo_url, latitude, longitude, status, dwell_platform_fee, min_dwell_platform_fee, min_dwell_platform_fee_percentage, referring_venue_incentive, created_at, points_redemption_schedule, closed_date)
            )
          `)
          .eq("user_id", user!.id);
        if (error) throw error;
        return data;
      });

      // Flatten nested structure: property_operators -> properties -> venues
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const allVenues: Venue[] = [];
      const seenIds = new Set<string>();
      for (const po of data ?? []) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const property = po.properties as any;
        if (!property?.venues) continue;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const v of property.venues as Venue[]) {
          if (v && !seenIds.has(v.id)) {
            seenIds.add(v.id);
            allVenues.push(v);
          }
        }
      }

      venueList = allVenues.filter((v) => USABLE_STATUSES.includes(v.status));
    }

    // Staff with no usable venues — force sign out (property_operators can have zero venues)
    if (profile?.role === "venue_staff" && venueList.length === 0) {
      console.warn("[VenueContext] No active venues — signing out");
      await signOut();
      window.location.replace("/login?suspended=true");
      return;
    }

    setVenues(venueList);

    // Restore previously selected venue or pick first (scoped per user)
    let selectedId: string | null = null;
    try {
      const userKey = selectedVenueKey(user!.id);
      selectedId = localStorage.getItem(userKey);
      // One-shot migration from legacy unscoped key
      if (!selectedId) {
        const legacy = localStorage.getItem(LEGACY_SELECTED_VENUE_KEY);
        if (legacy) {
          localStorage.setItem(userKey, legacy);
          localStorage.removeItem(LEGACY_SELECTED_VENUE_KEY);
          selectedId = legacy;
        }
      }
    } catch {
      // Storage disabled
    }

    const selectedVenue =
      venueList.find((v) => v.id === selectedId) ?? venueList[0] ?? null;
    setCurrentVenue(selectedVenue);
    setLoading(false);
  }, [user, profile, signOut]);

  useLayoutEffect(() => {
    if (!user || !profile) {
      setVenues([]);
      setCurrentVenue(null);
      setLoading(false);
      return;
    }
    setLoading(true);
  }, [user, profile]);

  useEffect(() => {
    if (!user || !profile) return;
    void loadVenues();
  }, [user, profile, loadVenues]);

  // ─── Realtime: listen for venue suspension broadcasts ──────────────────────
  const venuesRef = useRef<Venue[]>([]);
  useEffect(() => { venuesRef.current = venues; }, [venues]);

  const currentVenueRef = useRef<Venue | null>(null);
  useEffect(() => { currentVenueRef.current = currentVenue; }, [currentVenue]);

  useEffect(() => {
    if (!user || !profile || venues.length === 0) return;

    const supabase = getSupabase();
    const channels = venues.map((v) =>
      supabase
        .channel(`venue-suspended:${v.id}`)
        .on("broadcast", { event: "venue-suspended" }, (msg: { payload?: { venue_id?: string } }) => {
          const suspendedId = msg.payload?.venue_id;
          if (!suspendedId) return;

          console.warn("[VenueContext] Received venue-suspended broadcast for", suspendedId);

          if (profile.role === "venue_staff") {
            // Staff → sign out immediately
            console.warn("[VenueContext] Staff venue suspended — signing out");
            signOut().then(() => window.location.replace("/login?suspended=true"));
            return;
          }

          // Owner → remove suspended venue from list
          const remaining = venuesRef.current.filter((rv) => rv.id !== suspendedId);
          if (remaining.length === 0) {
            console.warn("[VenueContext] All owner venues suspended — signing out");
            signOut().then(() => window.location.replace("/login?suspended=true"));
            return;
          }

          setVenues(remaining);
          // If current venue was the suspended one, switch to another
          if (currentVenueRef.current?.id === suspendedId) {
            const next = remaining[0];
            setCurrentVenue(next);
            try { localStorage.setItem(selectedVenueKey(user.id), next.id); } catch { /* */ }
          }
        })
        .subscribe()
    );

    return () => {
      channels.forEach((ch) => supabase.removeChannel(ch));
    };
  }, [user, profile, venues.length, signOut]);

  // ─── Realtime: staff suspension/removal broadcast (TEC-238) ───────────────
  useEffect(() => {
    if (!user || profile?.role !== "venue_staff") return;

    const supabase = getSupabase();
    const channel = supabase
      .channel(`staff-status:${user.id}`)
      .on(
        "broadcast",
        { event: "staff-status-changed" },
        (msg: { payload?: { status?: string } }) => {
          const newStatus = msg.payload?.status;
          if (newStatus !== "suspended" && newStatus !== "removed") return;

          console.warn("[VenueContext] Received staff-status-changed broadcast:", newStatus);
          signOut().then(() => window.location.replace("/login?suspended=true"));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, profile?.role, signOut]);

  const switchVenue = useCallback(
    (venueId: string) => {
      const venue = venues.find((v) => v.id === venueId);
      if (venue && user) {
        setCurrentVenue(venue);
        try {
          localStorage.setItem(selectedVenueKey(user.id), venueId);
        } catch {
          // Storage disabled
        }
      }
    },
    [venues, user]
  );

  const patchCurrentVenue = useCallback(
    (patch: Partial<Venue>) => {
      setCurrentVenue((prev) => (prev ? { ...prev, ...patch } : prev));
      setVenues((prev) =>
        prev.map((v) => (v.id === currentVenue?.id ? { ...v, ...patch } : v))
      );
    },
    [currentVenue?.id]
  );

  return (
    <VenueContext.Provider value={{ venues, currentVenue, switchVenue, patchCurrentVenue, loading }}>
      {children}
    </VenueContext.Provider>
  );
}
